import { useMemo } from 'react';
import _ from 'lodash';

import { DataServiceFactory, useDataService } from './ApiDataService';

const baseUrl = '/ipm/v1/dashboards';

/**
 * Pages have an `_id` property, which is ugly to work with.  This
 * converts that to an `id` property.
 *
 * @param {object} page The page to add the `id` property to.
 *
 * @returns {object} The page.
 */
export function ensureId(page) {
    if (!page.id) {
        return _.defaults(page, {
            // eslint-disable-next-line no-underscore-dangle
            id: page._id,
        });
    }

    return page;
}

export default function DashboardServiceFactory(dataService = DataServiceFactory()) {
    function get(pageId) {
        return dataService.get(`${baseUrl}/${pageId}`).then(ensureId);
    }

    function remove(pageId) {
        return dataService.remove(`${baseUrl}/${pageId}`);
    }

    function save(page) {
        const isNew = !page._id;
        const url = `${baseUrl}${isNew ? '' : `/${page._id}`}`;
        return dataService[isNew ? 'post' : 'put'](
            url,
            page,
            {},
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );
    }

    function share(pageId, shareData) {
        const url = `${baseUrl}/${pageId}/share`;

        return dataService.post(
            url,
            shareData,
            {},
            {
                headers: { 'Content-Type': 'application/json' },
            }
        );
    }

    return {
        get,
        remove,
        save,
        share,
    };
}

export function useDashboardService() {
    const dataService = useDataService();
    return useMemo(() => DashboardServiceFactory(dataService), [dataService]);
}
